import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type PropertyStatus = 'all' | 'active' | 'pending' | 'sold' | 'expired' | 'draft';

interface UseActiveDealsParams {
  page: number;
  pageSize: number;
  status: PropertyStatus;
  searchTerm?: string;
}

interface UseActiveDealsResult {
  properties: any[];
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
}

export const useActiveDeals = ({
  page,
  pageSize,
  status,
  searchTerm = ''
}: UseActiveDealsParams): UseActiveDealsResult => {
  const { user } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['properties-paginated', user?.id, page, pageSize, status, searchTerm],
    queryFn: async () => {
      if (!user?.id) return { properties: [], totalCount: 0 };
      
      const from = page * pageSize;
      const to = from + pageSize - 1;
      const today = new Date().toISOString().split('T')[0];
      
      let query = supabase
        .from('properties')
        .select('*', { count: 'exact' })
        .eq('seller_id', user.id);
      
      // Apply search filter
      if (searchTerm) {
        query = query.or(`address.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`);
      }
      
      // Apply status filter using server-side logic
      switch (status) {
        case 'active':
          query = query
            .eq('status', 'active')
            .is('closed_date', null)
            .or(`contract_expiration_date.is.null,contract_expiration_date.gt.${today}`);
          break;
        case 'pending':
          query = query
            .eq('status', 'pending')
            .is('closed_date', null)
            .or(`contract_expiration_date.is.null,contract_expiration_date.gt.${today}`);
          break;
        case 'sold':
          query = query.not('closed_date', 'is', null);
          break;
        case 'expired':
          query = query
            .is('closed_date', null)
            .not('contract_expiration_date', 'is', null)
            .lte('contract_expiration_date', today);
          break;
        case 'draft':
          query = query
            .eq('status', 'draft')
            .is('closed_date', null);
          break;
        // 'all' - no additional filters
      }
      
      const { data, count, error } = await query
        .order('created_at', { ascending: false })
        .range(from, to);
      
      if (error) {
        console.error('Error fetching properties:', error);
        throw error;
      }
      
      return {
        properties: data || [],
        totalCount: count || 0
      };
    },
    enabled: !!user?.id
  });

  return {
    properties: data?.properties || [],
    totalCount: data?.totalCount || 0,
    isLoading,
    error: error as Error | null
  };
};
